import React, { useState, useEffect, useRef, memo } from 'react';
import { Mic, X, Loader2, Volume2, VolumeX, MicOff, Bot, Sparkles, RefreshCw, CheckCircle, ShoppingBag, Calendar, MessageSquare, Phone, Mail, Clock, CreditCard, Utensils, ChevronRight, Check, Languages } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { GoogleGenAI, LiveServerMessage, Modality, Type, FunctionDeclaration } from '@google/genai';
import { useCart } from '../context/CartContext';
import { ReservationForm, OrderForm, FeedbackForm, MenuDisplay, ComparisonDisplay } from './ChatForms';
import { CuisineCards, CategoryCards, ItemDetail } from './MenuHierarchy';
import { ReservationData, OrderFormData, FeedbackData, MenuItem } from '../types';
import {
  getMenuItems,
  checkAvailability as apiCheckAvailability,
  createOrGetCustomer,
  submitOrder as apiSubmitOrder,
  createReservation as apiCreateReservation,
  submitReview as apiSubmitReview,
  lookupReservations
} from '../api';

// --- Utils ---

function encode(bytes: Uint8Array) {
  let binary = '';
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function decode(base64: string) {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

function createPcmBlob(data: Float32Array): { data: string, mimeType: string } {
  const l = data.length;
  const int16 = new Int16Array(l);
  for (let i = 0; i < l; i++) {
    int16[i] = data[i] * 32768;
  }
  return {
    data: encode(new Uint8Array(int16.buffer)),
    mimeType: 'audio/pcm;rate=16000',
  };
}

async function decodeAudioData(
  data: Uint8Array,
  ctx: AudioContext,
  sampleRate: number,
  numChannels: number,
): Promise<AudioBuffer> {
  const dataInt16 = new Int16Array(data.buffer);
  const frameCount = dataInt16.length / numChannels;
  const buffer = ctx.createBuffer(numChannels, frameCount, sampleRate);

  for (let channel = 0; channel < numChannels; channel++) {
    const channelData = buffer.getChannelData(channel);
    for (let i = 0; i < frameCount; i++) {
      channelData[i] = dataInt16[i * numChannels + channel] / 32768.0;
    }
  }
  return buffer;
}

// --- Tool Definitions ---

const openFormTool: FunctionDeclaration = {
  name: 'openForm',
  description: 'Open a specific form modal or display for the user (Reservation, Order, Feedback, or Menu).',
  parameters: {
    type: Type.OBJECT,
    properties: {
      formType: { type: Type.STRING, enum: ['reservation', 'order', 'feedback', 'menu'] },
    },
    required: ['formType'],
  },
};

const closeFormTool: FunctionDeclaration = {
  name: 'closeForm',
  description: 'Close the currently open form.',
  parameters: { type: Type.OBJECT, properties: {} },
};

const closeChatTool: FunctionDeclaration = {
  name: 'closeChat',
  description: 'Close the voice assistant interface completely. Use this when the user says goodbye, indicates they are finished, or after a final transaction is completed and acknowledged.',
  parameters: { type: Type.OBJECT, properties: {} },
};

const fetchMenuItemsTool: FunctionDeclaration = {
  name: 'fetchMenuItems',
  description: 'Get list of menu items and display them. Can filter by cuisine, category, dietary restriction, or search query.',
  parameters: {
    type: Type.OBJECT,
    properties: {
      cuisine: { type: Type.STRING, description: 'Optional: Pakistani, Chinese, Continental, Fast Food, etc.' },
      category: { type: Type.STRING, description: 'Optional: BBQ, Karahi, Dessert, Beverage' },
      dietary: { type: Type.STRING, description: 'Optional: GF, V, VG' },
      query: { type: Type.STRING, description: 'Optional: Search term' },
    },
  },
};

const fetchCuisinesTool: FunctionDeclaration = {
  name: 'fetchCuisines',
  description: 'Display all available cuisine types (Pakistani, Chinese, etc.) as visual cards.',
  parameters: { type: Type.OBJECT, properties: {} },
};

const fetchCategoriesTool: FunctionDeclaration = {
  name: 'fetchCategories',
  description: 'Display all menu categories for a specific cuisine.',
  parameters: {
    type: Type.OBJECT,
    properties: {
      cuisine: { type: Type.STRING, description: 'The cuisine to fetch categories for (e.g. Pakistani)' },
    },
    required: ['cuisine'],
  },
};

const showItemDetailTool: FunctionDeclaration = {
  name: 'showItemDetail',
  description: 'Open a full detail view for a specific menu item.',
  parameters: {
    type: Type.OBJECT,
    properties: {
      itemName: { type: Type.STRING, description: 'Full name of the item to display' },
    },
    required: ['itemName'],
  },
};

const compareItemsTool: FunctionDeclaration = {
  name: 'compareItems',
  description: 'Visually compare two or more menu items side-by-side.',
  parameters: {
    type: Type.OBJECT,
    properties: {
      queries: {
        type: Type.ARRAY,
        items: { type: Type.STRING },
        description: 'List of names or keywords to find items to compare (e.g. ["burger", "salmon"])'
      },
    },
    required: ['queries'],
  },
};

const checkAvailabilityTool: FunctionDeclaration = {
  name: 'checkAvailability',
  description: 'Check if a reservation slot is available. Returns percentage full and busy status.',
  parameters: {
    type: Type.OBJECT,
    properties: {
      date: { type: Type.STRING, description: 'YYYY-MM-DD' },
      time: { type: Type.STRING, description: 'HH:mm' },
      partySize: { type: Type.NUMBER },
    },
    required: ['date', 'time', 'partySize'],
  },
};

const lookupReservationsTool: FunctionDeclaration = {
  name: 'lookupReservations',
  description: 'Check if a user already has existing reservations using their phone number.',
  parameters: {
    type: Type.OBJECT,
    properties: {
      phone: { type: Type.STRING, description: 'Phone number to search' },
    },
    required: ['phone'],
  },
};

const populateFormFieldTool: FunctionDeclaration = {
  name: 'populateFormField',
  description: 'Fill a specific field in the active form.',
  parameters: {
    type: Type.OBJECT,
    properties: {
      formType: { type: Type.STRING, enum: ['reservation', 'order', 'feedback'] },
      fieldName: { type: Type.STRING, description: 'Field to update (e.g. name, phone, date, time, guests, rating, review, email)' },
      value: { type: Type.STRING, description: 'Value to set. Convert numbers to strings.' },
    },
    required: ['formType', 'fieldName', 'value'],
  },
};

const placeOrderItemsTool: FunctionDeclaration = {
  name: 'placeOrderItems',
  description: 'Add items to the shopping cart.',
  parameters: {
    type: Type.OBJECT,
    properties: {
      items: {
        type: Type.ARRAY,
        items: {
          type: Type.OBJECT,
          properties: {
            name: { type: Type.STRING },
            quantity: { type: Type.NUMBER }
          },
          required: ['name']
        },
        description: 'List of objects with name and optional quantity'
      },
    },
    required: ['items'],
  },
};

const updateCartQuantityTool: FunctionDeclaration = {
  name: 'updateCartQuantity',
  description: 'Set the absolute quantity of a specific item in the cart. Use this when user says "make it 2" or "set quantity to 3".',
  parameters: {
    type: Type.OBJECT,
    properties: {
      itemName: { type: Type.STRING, description: 'Name of the item to update' },
      quantity: { type: Type.NUMBER, description: 'The new absolute quantity (e.g. 3)' }
    },
    required: ['itemName', 'quantity'],
  },
};

const validateFormTool: FunctionDeclaration = {
  name: 'validateForm',
  description: 'Check if the current form has all required fields filled.',
  parameters: {
    type: Type.OBJECT,
    properties: {
      formType: { type: Type.STRING, enum: ['reservation', 'order', 'feedback'] },
    },
    required: ['formType'],
  },
};

const submitOrderTool: FunctionDeclaration = {
  name: 'submitOrder',
  description: 'RESTRICTED: Finalize and submit the order to the database. Only use when user explicitly confirms submitting.',
  parameters: { type: Type.OBJECT, properties: {} },
};

const submitReservationTool: FunctionDeclaration = {
  name: 'submitReservation',
  description: 'RESTRICTED: Finalize and submit the reservation. Only use when user explicitly confirms.',
  parameters: { type: Type.OBJECT, properties: {} },
};

const submitReviewTool: FunctionDeclaration = {
  name: 'submitReview',
  description: 'Submit the feedback form. STRICT REQUIREMENT: User name and phone are required.',
  parameters: { type: Type.OBJECT, properties: {} },
};

const SYSTEM_INSTRUCTION = `
You are Maria, AI Maître d' at Zayka Palace. Warm, sales-driven Pakistani hospitality.

⚠️ CORE RULE: SHOW BEFORE SPEAKING ⚠️
1. Call tool FIRST to display UI
2. WAIT for UI to show
3. THEN speak about what's visible

NEVER mention prices, items, forms, or data without calling the tool first.

ORDERING FLOW:
"order/khana" → openForm("order") → showItemDetail(item) → placeOrderItems([{name, qty}]) → populateFormField → validateForm → submitOrder
- Always show item card BEFORE quoting price
- Update cart UI BEFORE mentioning totals

RESERVATION FLOW:
"table/booking" → openForm("reservation") → checkAvailability(date, time, size) → populateFormField → submitReservation
- Show availability BEFORE discussing slots

MENU NAVIGATION:
fetchCuisines() → fetchCategories(cuisine) → fetchMenuItems(category) → showItemDetail(item)
- Display cards FIRST, speak about max 3 items only

CART TOOLS (call BEFORE speaking):
- placeOrderItems([{name, qty}]) - add & show cart
- updateCartQuantity(item, newQty) - update & show total
- removeCartItem(item) - remove & update display

SALES TACTICS:
- Upsell: "Seekh Kabab pairs perfectly! Add?"
- Scarcity: "Only 3 left tonight!"
- Bundle: "2 mains = half-price dessert"
- Assumptive: "Shall I prepare that?"

LANGUAGE: 
- If user speaks Urdu → Respond in PURE Urdu (no English mixing)
- If user speaks English → Can mix Urdu words naturally (e.g., "Biryani bohut delicious hai!")

STRICT RULES:
- NEVER confirm order before submitOrder() success
- NEVER quote prices without showItemDetail
- NEVER invent menu items/prices
- NEVER speak about >3 items per turn
- ALWAYS call tools FIRST

AFTER SUCCESS: Ask for review → openForm("review") if yes

OFF-TOPIC: "I'm here for orders/reservations. What would you like?"

REMEMBER: TOOL FIRST → UI SHOWS → THEN SPEAK!
`;

const VoiceChatbot = () => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;

  const getSystemInstruction = () => `
${SYSTEM_INSTRUCTION}
`;

  const [isOpen, setIsOpen] = useState(false);
  const [activeForm, setActiveForm] = useState<'none' | 'reservation' | 'order' | 'feedback' | 'menu' | 'comparison'>('none');
  const [submissionResult, setSubmissionResult] = useState<{
    type: 'reservation' | 'order' | 'feedback';
    id?: string;
    details?: string;
  } | null>(null);

  const [visualMenuItems, setVisualMenuItems] = useState<MenuItem[]>([]);
  const [comparisonItems, setComparisonItems] = useState<MenuItem[]>([]);
  const [status, setStatus] = useState<'idle' | 'connecting' | 'listening' | 'speaking' | 'processing' | 'error' | 'denied'>('idle');
  const [isAiMuted, setIsAiMuted] = useState(false);
  const [isToolCalling, setIsToolCalling] = useState(false);

  // Menu Hierarchy States
  const [menuViewMode, setMenuViewMode] = useState<'all' | 'cuisines' | 'categories' | 'items' | 'detail'>('all');
  const [currentCuisine, setCurrentCuisine] = useState<string | null>(null);
  const [currentCategory, setCurrentCategory] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);

  // Data States
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [resData, setResData] = useState<ReservationData>({ date: '', time: '', guests: 2, name: '', phone: '', email: '', specialRequests: '' });
  const [orderData, setOrderData] = useState<OrderFormData>({ name: '', phone: '', email: '', pickupTime: 'ASAP', paymentMethod: 'pickup', specialInstructions: '' });
  const [feedbackData, setFeedbackData] = useState<FeedbackData>({ rating: 0, tags: [], review: '', allowContact: false, showName: true, name: '', phone: '', email: '' });

  // Context
  const { addToCart, cart, cartTotal, clearCart, setItemQuantity } = useCart();

  // Refs
  const cartRef = useRef(cart);
  const cartTotalRef = useRef(cartTotal);
  const resDataRef = useRef(resData);
  const orderDataRef = useRef(orderData);
  const feedbackDataRef = useRef(feedbackData);
  const menuItemsRef = useRef(menuItems);

  useEffect(() => {
    cartRef.current = cart;
    cartTotalRef.current = cartTotal;
    resDataRef.current = resData;
    orderDataRef.current = orderData;
    feedbackDataRef.current = feedbackData;
    menuItemsRef.current = menuItems;
  }, [cart, cartTotal, resData, orderData, feedbackData, menuItems]);

  const inputAudioContextRef = useRef<AudioContext | null>(null);
  const outputAudioContextRef = useRef<AudioContext | null>(null);
  const nextStartTimeRef = useRef<number>(0);
  const sourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set());
  const streamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const sessionRef = useRef<any>(null);

  useEffect(() => {
    getMenuItems().then(items => setMenuItems(items));
  }, []);

  const cleanup = () => {
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    if (processorRef.current) processorRef.current.disconnect();
    if (inputAudioContextRef.current?.state !== 'closed') inputAudioContextRef.current?.close();
    if (outputAudioContextRef.current?.state !== 'closed') outputAudioContextRef.current?.close();
    sourcesRef.current.forEach(s => { try { s.stop(); } catch { } });
    sourcesRef.current.clear();
    nextStartTimeRef.current = 0;
  };

  const stopAudioPlayback = () => {
    sourcesRef.current.forEach(source => {
      try { source.stop(); } catch (e) { /* ignore already stopped */ }
    });
    sourcesRef.current.clear();
    // Reset the scheduler pointer to current time to avoid large delays when resuming
    if (outputAudioContextRef.current) {
      nextStartTimeRef.current = outputAudioContextRef.current.currentTime;
    }
  };

  const closeSession = () => {
    if (sessionRef.current) {
      sessionRef.current.then((s: any) => s.close && s.close());
    }
    cleanup();
    setIsOpen(false);
    setActiveForm('none');
    setSubmissionResult(null);
    setStatus('idle');
  };

  const startSession = async () => {
    cleanup();
    setStatus('connecting');
    setSubmissionResult(null);

    try {
      if (!process.env.API_KEY) {
        console.error("API_KEY is missing. Please check your environment variables.");
        setStatus('error');
        return;
      }

      // Initialize Audio Contexts early
      inputAudioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 16000 });
      outputAudioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });

      const outputNode = outputAudioContextRef.current.createGain();
      outputNode.connect(outputAudioContextRef.current.destination);

      // Start stream request in parallel with AI connection
      const streamPromise = navigator.mediaDevices.getUserMedia({ audio: true });

      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });

      const sessionPromise = ai.live.connect({
        model: 'gemini-2.5-flash-native-audio-preview-12-2025',
        config: {
          responseModalities: [Modality.AUDIO],
          speechConfig: { voiceConfig: { prebuiltVoiceConfig: { voiceName: 'Kore' } } },
          systemInstruction: getSystemInstruction(),
          inputAudioTranscription: {},
          outputAudioTranscription: {},
          tools: [{
            functionDeclarations: [
              openFormTool, closeFormTool, closeChatTool, fetchMenuItemsTool,
              fetchCuisinesTool, fetchCategoriesTool, showItemDetailTool,
              compareItemsTool, checkAvailabilityTool,
              populateFormFieldTool, placeOrderItemsTool, updateCartQuantityTool, validateFormTool,
              submitOrderTool, submitReservationTool, submitReviewTool, lookupReservationsTool
            ]
          }],
        },
        callbacks: {
          onopen: async () => {
            try {
              // Wait for stream to be ready if it isn't already
              const stream = await streamPromise;
              streamRef.current = stream;

              setStatus('listening');

              if (!inputAudioContextRef.current) return;
              const source = inputAudioContextRef.current.createMediaStreamSource(stream);
              const scriptProcessor = inputAudioContextRef.current.createScriptProcessor(4096, 1, 1);
              processorRef.current = scriptProcessor;

              scriptProcessor.onaudioprocess = (e) => {
                const inputData = e.inputBuffer.getChannelData(0);
                const pcmBlob = createPcmBlob(inputData);
                sessionPromise.then(session => session.sendRealtimeInput({ media: pcmBlob }));
              };
              source.connect(scriptProcessor);
              scriptProcessor.connect(inputAudioContextRef.current.destination);

              // Send initial greeting so Maria speaks first
              sessionPromise.then((session: any) => {
                // Add a small delay to ensure session is fully ready
                setTimeout(() => {
                  session.sendRealtimeInput({
                    text: 'Greet the user warmly as Maria from Zayka Palace and ask how you can help them today.'
                  });
                }, 500);
              });
            } catch (err) {
              console.error("Stream initialization error:", err);
              setStatus('error');
              cleanup();
            }
          },
          onmessage: async (message: LiveServerMessage) => {
            // Handle User Interruption (Barge-in)
            if (message.serverContent?.interrupted) {
              console.log('User interrupted - stopping audio');
              stopAudioPlayback();
              setStatus('listening');
              return;
            }

            if (message.serverContent?.turnComplete) {
              setStatus('listening');
            }

            if (message.toolCall) {
              setStatus('processing');
              setIsToolCalling(true);
              const responses = [];

              for (const fc of message.toolCall.functionCalls) {
                // UI Tool Call Indicator
                console.log(`Maria is calling: ${fc.name}`);

                let result: any = { status: 'success' };
                const args = fc.args as any;

                try {
                  // --- Tool Handlers ---

                  if (fc.name === 'openForm') {
                    const type = args.formType;
                    // Auto-populate menu if opening menu and no items displayed yet
                    if (type === 'menu' && menuItemsRef.current.length > 0) {
                      setVisualMenuItems(menuItemsRef.current);
                    }
                    setSubmissionResult(null);
                    setActiveForm(type);
                    result = { status: 'opened', form: type };
                  }

                  else if (fc.name === 'closeForm') {
                    setActiveForm('none');
                    result = { status: 'closed' };
                  }

                  else if (fc.name === 'closeChat') {
                    result = { status: 'closing' };
                    // Wait a moment for the AI's farewell audio to play/start before cutting it off
                    setTimeout(() => {
                      closeSession();
                    }, 3000);
                  }

                  else if (fc.name === 'fetchMenuItems') {
                    const category = args.category?.toLowerCase();
                    const cuisine = args.cuisine?.toLowerCase();
                    const dietary = args.dietary?.toUpperCase();
                    const query = args.query?.toLowerCase();

                    let filtered = menuItemsRef.current;

                    if (cuisine) {
                      filtered = filtered.filter(i => i.cuisine?.toLowerCase() === cuisine);
                    }
                    if (category) {
                      filtered = filtered.filter(i => i.category.toLowerCase() === category);
                    }
                    if (dietary) {
                      filtered = filtered.filter(i => i.dietary?.includes(dietary));
                    }
                    if (query) {
                      filtered = filtered.filter(i =>
                        i.name.toLowerCase().includes(query) ||
                        i.description.toLowerCase().includes(query)
                      );
                    }

                    setVisualMenuItems(filtered);
                    setMenuViewMode('items');
                    setActiveForm('menu');
                    const itemsSummary = filtered.slice(0, 3).map(i => ({
                      name: i.name,
                      price: i.price,
                      description: i.description,
                      category: i.category,
                      dietary: i.dietary
                    }));
                    result = {
                      itemsShown: itemsSummary,
                      totalItems: filtered.length,
                      message: `Displayed ${filtered.length} items. Spoken 3.`
                    };
                  }

                  else if (fc.name === 'fetchCuisines') {
                    const cuisines = Array.from(new Set(menuItemsRef.current.map(i => i.cuisine).filter(Boolean)));
                    setMenuViewMode('cuisines');
                    setActiveForm('menu');
                    result = { cuisines };
                  }

                  else if (fc.name === 'fetchCategories') {
                    const cuisine = args.cuisine;
                    const categories = Array.from(new Set(
                      menuItemsRef.current
                        .filter(i => i.cuisine === cuisine)
                        .map(i => i.category)
                    ));
                    setCurrentCuisine(cuisine);
                    setMenuViewMode('categories');
                    setActiveForm('menu');
                    result = { cuisine, categories };
                  }

                  else if (fc.name === 'showItemDetail') {
                    const item = menuItemsRef.current.find(i => i.name.toLowerCase() === args.itemName.toLowerCase());
                    if (item) {
                      setSelectedItem(item);
                      setMenuViewMode('detail');
                      setActiveForm('menu');
                      result = { item };
                    } else {
                      result = { status: 'error', message: 'Item not found' };
                    }
                  }

                  else if (fc.name === 'compareItems') {
                    const queries = args.queries || [];
                    const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

                    const foundItems: MenuItem[] = [];
                    for (const q of queries) {
                      const nQ = normalize(q);
                      const match = menuItemsRef.current.find(m => {
                        const mName = normalize(m.name);
                        return mName.includes(nQ) || nQ.includes(mName);
                      });
                      if (match && !foundItems.find(i => i.id === match.id)) {
                        foundItems.push(match);
                      }
                    }

                    if (foundItems.length > 0) {
                      setComparisonItems(foundItems);
                      setActiveForm('comparison');
                      result = {
                        status: 'comparing',
                        items: foundItems.map(i => ({
                          name: i.name,
                          price: i.price,
                          description: i.description,
                          dietary: i.dietary
                        }))
                      };
                    } else {
                      result = { status: 'error', message: 'Could not find items to compare' };
                    }
                  }

                  else if (fc.name === 'checkAvailability') {
                    result = await apiCheckAvailability(args.date, args.time, args.partySize);
                  }

                  else if (fc.name === 'lookupReservations') {
                    const reservations = await lookupReservations(args.phone);
                    result = {
                      found: reservations.length > 0,
                      count: reservations.length,
                      reservations: reservations.map(r => ({ date: r.reservation_date, time: r.reservation_time, guests: r.party_size }))
                    };
                  }

                  else if (fc.name === 'populateFormField') {
                    const { formType, fieldName, value } = args;
                    // Proactive Visual Sync: Open the form if it's being populated
                    setActiveForm(formType);

                    if (formType === 'reservation') {
                      setResData(prev => ({ ...prev, [fieldName]: fieldName === 'guests' ? Number(value) : value }));
                    } else if (formType === 'order') {
                      setOrderData(prev => ({ ...prev, [fieldName]: value }));
                    } else if (formType === 'feedback') {
                      setFeedbackData(prev => ({
                        ...prev,
                        [fieldName]: fieldName === 'rating' ? Number(value) : (fieldName === 'tags' ? value.split(',') : value)
                      }));
                    }
                    result = { status: 'updated', field: fieldName, value, ui_sync: true };
                  }

                  else if (fc.name === 'placeOrderItems') {
                    const itemsToAdd = args.items || [];
                    const added = [];
                    const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

                    for (const itemObj of itemsToAdd) {
                      const nName = normalize(itemObj.name);
                      const qty = itemObj.quantity || 1;
                      const match = menuItemsRef.current.find(m => {
                        const mName = normalize(m.name);
                        return mName.includes(nName) || nName.includes(mName);
                      });
                      if (match) {
                        addToCart(match, qty);
                        added.push(`${match.name} (x${qty})`);
                      }
                    }
                    setActiveForm('order'); // Auto-switch to order form (cart view)
                    result = { addedItems: added, count: added.length };
                  }

                  else if (fc.name === 'updateCartQuantity') {
                    const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
                    const nName = normalize(args.itemName);
                    const match = cartRef.current.find(m => {
                      const mName = normalize(m.name);
                      return mName.includes(nName) || nName.includes(mName);
                    });

                    if (match) {
                      setItemQuantity(match.id, args.quantity);
                      setActiveForm('order'); // Auto-switch to order form to see changes
                      result = { status: 'success', item: match.name, newQuantity: args.quantity };
                    } else {
                      result = { status: 'error', message: 'Item not found in cart' };
                    }
                  }

                  else if (fc.name === 'validateForm') {
                    const missing = [];
                    if (args.formType === 'order') {
                      if (!orderDataRef.current.name) missing.push('name');
                      if (!orderDataRef.current.phone) missing.push('phone');
                      if (cartRef.current.length === 0) missing.push('items');
                    } else if (args.formType === 'reservation') {
                      if (!resDataRef.current.name) missing.push('name');
                      if (!resDataRef.current.phone) missing.push('phone');
                      if (!resDataRef.current.date) missing.push('date');
                      if (!resDataRef.current.time) missing.push('time');
                    } else if (args.formType === 'feedback') {
                      if (!feedbackDataRef.current.rating) missing.push('rating');
                      if (!feedbackDataRef.current.name) missing.push('name');
                      if (!feedbackDataRef.current.phone) missing.push('phone');
                    }
                    result = { isValid: missing.length === 0, missingFields: missing };
                  }

                  else if (fc.name === 'submitOrder') {
                    const data = orderDataRef.current;

                    // VALIDATION CHECK
                    if (!data.name || !data.phone) {
                      // Force open the form so user can see fields
                      if (activeForm !== 'order') setActiveForm('order');
                      throw new Error("Missing Name or Phone Number. Please ask the user for these details before submitting.");
                    }

                    const customer = await createOrGetCustomer(data.name, data.phone, data.email);
                    if (!customer) throw new Error('Could not create customer profile');
                    const order = await apiSubmitOrder(customer, cartRef.current, data, cartTotalRef.current);
                    clearCart();
                    setSubmissionResult({ type: 'order', id: order.order_number });
                    setActiveForm('none');
                    result = { success: true, orderNumber: order.order_number };
                  }

                  else if (fc.name === 'submitReservation') {
                    const data = resDataRef.current;

                    // VALIDATION CHECK
                    if (!data.name || !data.phone || !data.date || !data.time) {
                      if (activeForm !== 'reservation') setActiveForm('reservation');
                      throw new Error("Missing reservation details (Name, Phone, Date, or Time). Please collect these before submitting.");
                    }

                    const customer = await createOrGetCustomer(data.name, data.phone, data.email);
                    if (!customer) throw new Error('Could not create customer profile');
                    const res = await apiCreateReservation(customer, data);
                    setSubmissionResult({ type: 'reservation', id: res.reservation_code, details: `${data.date} at ${data.time}` });
                    setActiveForm('none');
                    result = { success: true, code: res.reservation_code };
                  }

                  else if (fc.name === 'submitReview') {
                    const data = feedbackDataRef.current;
                    if (!data.name || !data.phone) {
                      if (activeForm !== 'feedback') setActiveForm('feedback');
                      throw new Error("Missing user details. You must ask the user for their Name and Phone Number before submitting the review.");
                    }
                    const customer = await createOrGetCustomer(data.name, data.phone, data.email);
                    await apiSubmitReview(customer, data);
                    setSubmissionResult({ type: 'feedback' });
                    setActiveForm('none');
                    result = { success: true };
                  }

                } catch (e: any) {
                  console.error(`Tool error (${fc.name}):`, e);
                  result = { error: e.message || "Operation failed" };
                }

                responses.push({
                  id: fc.id,
                  name: fc.name,
                  response: { result }
                });
              }

              sessionPromise.then(session => session.sendToolResponse({ functionResponses: responses }));
              setIsToolCalling(false);
            }

            // Audio Playback
            const base64Audio = message.serverContent?.modelTurn?.parts?.[0]?.inlineData?.data;
            if (base64Audio && outputAudioContextRef.current) {
              setStatus('speaking');
              const ctx = outputAudioContextRef.current;
              nextStartTimeRef.current = Math.max(nextStartTimeRef.current, ctx.currentTime);
              const buffer = await decodeAudioData(decode(base64Audio), ctx, 24000, 1);
              const source = ctx.createBufferSource();
              source.buffer = buffer;
              source.connect(outputNode);
              source.start(nextStartTimeRef.current);
              nextStartTimeRef.current += buffer.duration;
              sourcesRef.current.add(source);
              source.onended = () => {
                sourcesRef.current.delete(source);
                if (sourcesRef.current.size === 0) setStatus('listening');
              };
            }
          },
          onerror: (e) => { console.error(e); setStatus('error'); cleanup(); },
          onclose: () => { setStatus('idle'); cleanup(); }
        }
      });
      sessionRef.current = sessionPromise;

      // Handle mic permission errors
      streamPromise.catch(e => {
        console.error("Mic access denied or failed", e);
        setStatus('denied');
        sessionPromise.then(s => s.close && s.close()).catch(() => { });
        cleanup();
      });

    } catch (err: any) {
      console.error(err);
      setStatus(err.name === 'NotAllowedError' ? 'denied' : 'error');
      cleanup();
    }
  };

  const handleToggle = () => {
    if (isOpen) {
      closeSession();
    } else {
      setIsOpen(true);
      startSession();
    }
  };

  const closeForm = () => setActiveForm('none');

  const handleManualSubmit = (data: { type: string, data: any, result: any }) => {
    closeForm();

    // Set UI Submission Result
    if (data.type === 'reservation') {
      setSubmissionResult({ type: 'reservation', id: data.result?.reservation_code, details: `${data.data.date} at ${data.data.time}` });
    } else if (data.type === 'order') {
      setSubmissionResult({ type: 'order', id: data.result?.order_number });
    } else if (data.type === 'feedback') {
      setSubmissionResult({ type: 'feedback' });
    }

    // Inject system event into AI session to synchronize state
    if (sessionRef.current) {
      sessionRef.current.then((session: any) => {
        let text = "";
        if (data.type === 'reservation') {
          text = `[System Event] User has manually successfully completed the reservation. Code: ${data.result?.reservation_code || 'Confirmed'}. Date: ${data.data.date}, Time: ${data.data.time}. Please acknowledge this to the user immediately and ask for a review.`;
        } else if (data.type === 'order') {
          text = `[System Event] User has manually successfully placed the order. Order #${data.result?.order_number || 'Confirmed'}. Please confirm, thank the user, and ask for a review.`;
        } else if (data.type === 'feedback') {
          text = `[System Event] User has manually submitted feedback with rating ${data.data.rating}. Thank the user warmly.`;
        }

        if (text) {
          // Send text as user input to update context
          session.sendRealtimeInput({ text });
        }
      });
    }
  };

  const toggleAiMute = () => {
    if (outputAudioContextRef.current) {
      if (isAiMuted) {
        outputAudioContextRef.current.resume();
      } else {
        outputAudioContextRef.current.suspend();
      }
      setIsAiMuted(!isAiMuted);
    }
  };

  const resetSession = () => {
    if (sessionRef.current) {
      sessionRef.current.then((s: any) => s.close && s.close());
    }
    startSession();
  };

  return (
    <>
      {!isOpen && (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2 group">
          {/* Notification Badge */}
          <div className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full animate-bounce shadow-lg transform translate-y-2 z-10">
            1 New
          </div>
          <button
            onClick={handleToggle}
            className="bg-terracotta hover:bg-terracotta-dark text-white px-6 py-4 rounded-full shadow-2xl transition-all flex items-center gap-3 font-bold animate-breathing group hover:w-48 overflow-hidden"
          >
            <Bot size={24} className="group-hover:animate-bounce flex-shrink-0" />
            <span className="whitespace-nowrap opacity-100 group-hover:block">
              {currentLang === 'ur' ? 'ماریا سے پوچھیں' : 'Ask Maria'}
            </span>
          </button>
        </div>
      )}

      {isOpen && (
        <div className="fixed inset-0 z-[100] bg-mocha-light/98 backdrop-blur-2xl flex flex-col animate-in fade-in duration-300 pb-[env(safe-area-inset-bottom)]">

          {/* Header */}
          <div className="flex-shrink-0 p-4 md:p-6 flex justify-between items-center bg-white/50 backdrop-blur-sm border-b border-white/20">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-terracotta flex items-center justify-center text-white shadow-inner border border-white/20">
                <Bot size={24} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="font-serif font-bold text-xl md:text-2xl text-gray-900">Maria</h2>
                  <div className={`w-2.5 h-2.5 rounded-full ${status === 'error' ? 'bg-red-500' : status === 'connecting' ? 'bg-gold animate-pulse' : 'bg-green-500'} shadow-[0_0_8px_rgba(34,197,94,0.5)]`} />
                </div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Maître d' • Zayka Palace</p>
              </div>
            </div>
            <button onClick={handleToggle} className="p-2 rounded-full hover:bg-black/5 text-gray-500 transition-colors">
              <X size={28} />
            </button>
          </div>

          <div className="flex-grow flex flex-col md:flex-row overflow-hidden relative">

            {/* Visualizer Panel - Centered content */}
            <div className={`flex flex-col items-center justify-center transition-all duration-500 ${activeForm !== 'none' || submissionResult ? 'md:w-1/3 border-r border-white/20' : 'w-full'}`}>

              <div className="flex flex-col items-center gap-8 animate-in zoom-in-95 duration-500 p-8">
                {/* Orb */}
                <div className="relative">
                  <div className={`absolute inset-0 rounded-full border-2 border-terracotta/20 scale-150 ${status === 'speaking' ? 'animate-ping' : ''}`} />
                  <div className={`relative w-24 h-24 md:w-32 md:h-32 rounded-full flex items-center justify-center shadow-2xl transition-all duration-500 ${status === 'speaking' ? 'bg-terracotta scale-110' :
                    status === 'listening' ? 'bg-teal' :
                      status === 'processing' || status === 'connecting' ? 'bg-gold' :
                        status === 'error' || status === 'denied' ? 'bg-red-500' : 'bg-gray-400'
                    }`}>
                    {status === 'speaking' ? <Volume2 size={48} className="text-white animate-pulse" /> :
                      status === 'listening' ? <Mic size={48} className="text-white" /> :
                        status === 'processing' || status === 'connecting' ? <Loader2 size={48} className="text-white animate-spin" /> :
                          <Bot size={48} className="text-white" />}
                  </div>
                </div>

                {isToolCalling && (
                  <div className="flex items-center gap-2 bg-white/80 backdrop-blur-md px-4 py-1.5 rounded-full shadow-sm border border-terracotta/10 animate-in fade-in slide-in-from-bottom-2">
                    <RefreshCw size={14} className="animate-spin text-terracotta" />
                    <span className="text-[10px] font-bold text-terracotta uppercase tracking-wider">Checking...</span>
                  </div>
                )}

                {/* Status Text */}
                <div className="text-center space-y-2">
                  <h3 className="font-serif text-2xl font-bold text-gray-800">
                    {status === 'listening' ? 'Listening...' :
                      status === 'speaking' ? 'Speaking...' :
                        status === 'processing' ? 'Thinking...' :
                          status === 'connecting' ? 'Connecting...' :
                            status === 'error' ? 'Error' : 'Ready'}
                  </h3>
                  {status === 'processing' && (
                    <div className="animate-typing flex justify-center mt-2">
                      <span></span><span></span><span></span>
                    </div>
                  )}
                  <p className="text-sm text-gray-500 font-medium">
                    {status === 'idle' ? 'Tap the microphone to start' : 'Go ahead, I\'m listening'}
                    {status === 'error' && <span className="block text-red-500 text-xs mt-1">Connection failed. Check API Key.</span>}
                  </p>
                </div>

                {/* Controls */}
                <div className="flex items-center gap-6 mt-4">
                  <button
                    onClick={toggleAiMute}
                    className={`p-4 rounded-full shadow-lg transition-all hover:scale-105 active:scale-95 ${isAiMuted ? 'bg-red-100 text-red-500' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    title={isAiMuted ? "Unmute AI" : "Mute AI"}
                  >
                    {isAiMuted ? <VolumeX size={24} /> : <Volume2 size={24} />}
                  </button>

                  <button
                    onClick={() => {
                      if (status === 'idle' || status === 'error') startSession();
                    }}
                    className={`p-6 rounded-full shadow-xl transition-all transform hover:scale-105 active:scale-95 ${status === 'listening' ? 'bg-terracotta text-white ring-4 ring-terracotta/20' : 'bg-white text-gray-400'}`}
                  >
                    {status === 'listening' ? <Mic size={32} /> : <MicOff size={32} />}
                  </button>

                  <button
                    onClick={resetSession}
                    className="p-4 rounded-full bg-white text-gray-600 shadow-lg hover:bg-gray-50 hover:text-terracotta transition-all hover:scale-105 active:scale-95"
                    title="Restart Session"
                  >
                    <RefreshCw size={24} />
                  </button>
                </div>
              </div>
            </div>

            {/* Dynamic Forms or Success Panel */}
            {(activeForm !== 'none' || submissionResult) && (
              <div className="absolute inset-0 md:static md:w-2/3 z-10 flex items-end md:items-center justify-center p-4 pointer-events-none">
                <div className={`pointer-events-auto w-full max-w-lg h-[85vh] md:h-[90%] bg-white/95 backdrop-blur-2xl rounded-3xl shadow-2xl border border-white/50 overflow-hidden flex flex-col transform transition-all duration-500 animate-in slide-in-from-bottom-8 md:slide-in-from-right-8 fade-in shadow-terracotta/10 relative ${status === 'listening' ? 'pulse-ring' : ''}`}>

                  {activeForm !== 'none' ? (
                    <>
                      {activeForm === 'reservation' && (
                        <ReservationForm
                          data={resData}
                          onChange={(f, v) => setResData(p => ({ ...p, [f]: v }))}
                          onSubmit={handleManualSubmit}
                          onCancel={closeForm}
                        />
                      )}
                      {activeForm === 'order' && (
                        <OrderForm
                          data={orderData}
                          onChange={(f, v) => setOrderData(p => ({ ...p, [f]: v }))}
                          onSubmit={handleManualSubmit}
                          onCancel={closeForm}
                        />
                      )}
                      {activeForm === 'feedback' && (
                        <FeedbackForm
                          data={feedbackData}
                          onChange={(f, v) => setFeedbackData(p => ({ ...p, [f]: v }))}
                          onSubmit={handleManualSubmit}
                          onCancel={closeForm}
                        />
                      )}
                      {activeForm === 'menu' && (
                        <>
                          {menuViewMode === 'cuisines' && (
                            <CuisineCards
                              cuisines={Array.from(new Set(menuItems.map(i => i.cuisine).filter(Boolean))) as string[]}
                              onSelect={(cuisine) => {
                                // User selected a cuisine, fetch categories
                                const categories = Array.from(new Set(
                                  menuItems.filter(i => i.cuisine === cuisine).map(i => i.category)
                                ));
                                setCurrentCuisine(cuisine);
                                setMenuViewMode('categories');
                              }}
                              onCancel={closeForm}
                            />
                          )}
                          {menuViewMode === 'categories' && currentCuisine && (
                            <CategoryCards
                              cuisine={currentCuisine}
                              categories={Array.from(new Set(
                                menuItems.filter(i => i.cuisine === currentCuisine).map(i => i.category)
                              ))}
                              onSelect={(category) => {
                                // User selected a category, fetch items
                                const filtered = menuItems.filter(i => i.category === category);
                                setVisualMenuItems(filtered);
                                setCurrentCategory(category);
                                setMenuViewMode('items');
                              }}
                              onBack={() => setMenuViewMode('cuisines')}
                              onCancel={closeForm}
                            />
                          )}
                          {menuViewMode === 'items' && (
                            <MenuDisplay
                              items={visualMenuItems}
                              onAddToCart={(item) => addToCart(item)}
                              onCancel={closeForm}
                            />
                          )}
                          {menuViewMode === 'detail' && selectedItem && (
                            <ItemDetail
                              item={selectedItem}
                              onAddToCart={(item) => {
                                addToCart(item);
                                setMenuViewMode('items');
                              }}
                              onBack={() => setMenuViewMode('items')}
                              onCancel={closeForm}
                            />
                          )}
                        </>
                      )}
                      {activeForm === 'comparison' && (
                        <ComparisonDisplay
                          items={comparisonItems}
                          onAddToCart={(item) => addToCart(item)}
                          onCancel={closeForm}
                        />
                      )}
                    </>
                  ) : (
                    // Success Result View
                    <div className="flex flex-col items-center justify-center h-full text-center p-8 space-y-6 animate-in zoom-in-95 relative overflow-hidden">
                      {/* CSS Confetti */}
                      <div className="confetti-container">
                        {[...Array(20)].map((_, i) => (
                          <div
                            key={i}
                            className="confetti-piece"
                            style={{
                              left: `${Math.random() * 100}%`,
                              animationDelay: `${Math.random() * 3}s`,
                              animationDuration: `${2 + Math.random() * 2}s`
                            }}
                          />
                        ))}
                      </div>

                      <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center shadow-lg mb-2 relative z-10">
                        <CheckCircle className="text-green-600 w-12 h-12" />
                      </div>

                      <div className="relative z-10">
                        <h3 className="font-serif text-3xl font-bold text-gray-900 mb-2">
                          {submissionResult?.type === 'reservation' ? 'Table Reserved!' :
                            submissionResult?.type === 'order' ? 'Order Placed!' :
                              'Review Submitted!'}
                        </h3>
                        <p className="text-[10px] font-bold text-terracotta uppercase tracking-[0.2em] mb-4">
                          {submissionResult?.type === 'reservation' ? 'ٹیبل بک ہو گیا' :
                            submissionResult?.type === 'order' ? 'آرڈر دے دیا گیا' :
                              'جائزہ جمع کر دیا گیا'}
                        </p>
                        <p className="text-gray-500 font-medium">
                          {submissionResult?.type === 'feedback'
                            ? 'Thank you for your feedback. (آپ کی رائے کا شکریہ)'
                            : 'Your request has been successfully processed. (آپ کی درخواست پر عمل کر دیا گیا ہے)'}
                        </p>
                      </div>

                      {submissionResult?.id && (
                        <div className="bg-gray-50 border border-gray-100 rounded-2xl p-6 w-full max-w-sm mx-auto">
                          <div className="text-xs text-gray-400 font-bold uppercase tracking-widest mb-1">
                            {submissionResult.type === 'reservation' ? 'Reservation Code' : 'Order Number'}
                          </div>
                          <div className="text-2xl font-mono font-bold text-terracotta tracking-wider select-all">
                            {submissionResult.id}
                          </div>
                          {submissionResult.details && (
                            <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-center gap-2 text-gray-600 text-sm font-medium">
                              <Calendar size={16} /> {submissionResult.details}
                            </div>
                          )}
                        </div>
                      )}

                      {submissionResult?.type !== 'feedback' && (
                        <div className="bg-terracotta/5 p-4 rounded-xl text-sm text-gray-600 max-w-sm">
                          <MessageSquare size={16} className="inline mr-2 text-terracotta" />
                          <span className="font-bold text-terracotta">Maria:</span> "Would you like to leave a review? (کیا آپ جائزہ لینا چاہیں گے؟)"
                        </div>
                      )}

                      <button
                        onClick={() => setSubmissionResult(null)}
                        className="text-gray-400 hover:text-gray-600 text-sm font-medium mt-8"
                      >
                        Close Confirmation
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default memo(VoiceChatbot);